import { Form, Formik } from 'formik';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, ProgressBar, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import pspLogo from '../../../assets/PSP.svg';
import FileInput from '../../../components/Input/FileInput';
import NavigationFooter from '../../../components/NavigationFooter/NavigationFooter';
import { updateBusinessDocuments } from '../../../services/commerce.service';
import {
  AddCommerceSixthAndFinalStepLocationState,
  BeneficiaryId,
  NewBeneficiaryValues,
} from '../../../types/add_commerce.types';
import { UpdateFilesRequest } from '../../../types/business/request';
import { FormStatusEnum } from '../../../types/enums/form-status.enum';
import { doRefsSubmissions } from '../../../utils/formikHelpers';
import { getBeneficiaries, triggerToasts } from '../../../utils/helpers';
import { pagesPaths } from '../../../utils/navigationUtils';
import { getProgressPercentage } from '../../../utils/progressHelper';
import schemas from '../../../utils/schemas';
import NewBeneficiaryId from './NewBeneficiaryId';

function CommerceDocumentation() {
  const location = useLocation();
  const navigate = useNavigate();
  const localState = location.state as AddCommerceSixthAndFinalStepLocationState;
  const formRefs = useRef<any>([]);
  const [cardFormDGI, setCardFormDGI] = useState<File | string | null>(localState?.cardFormDGI || null);
  const [extraDocumentOne, setExtraDocumentOne] = useState<File | string | null>(
    localState?.extraDocumentOne || null,
  );
  const [extraDocumentTwo, setExtraDocumentTwo] = useState<File | string | null>(
    localState?.extraDocumentTwo || null,
  );
  const [extraDocumentThree, setExtraDocumentThree] = useState<File | string | null>(
    localState?.extraDocumentThree || null,
  );
  const beneficiaries = useMemo(() => getBeneficiaries(localState?.branches), [localState?.branches]);
  const [beneficiaryIds, setBeneficiaryIds] = useState<Array<BeneficiaryId>>(
    beneficiaries.map((beneficiary: NewBeneficiaryValues) => ({
      id: beneficiary.id!,
      frontDocument: beneficiary.frontDocument!,
      backDocument: beneficiary.backDocument!,
    })),
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Documents can no longer be edited once the onboarding is finished; redirect back to the
  // summary regardless of how this route was reached (deep link, browser back, stale state).
  const isFinished = localState?.status === FormStatusEnum[FormStatusEnum.FINISHED];

  useEffect(() => {
    if (isFinished) {
      navigate(pagesPaths.CommerceSummary, { state: { ...localState, hideEmailSection: true } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished]);

  const postFiles = async () => {
    const fileBody: UpdateFilesRequest = {
      rut: cardFormDGI!,
      beneficiaryIds: beneficiaryIds?.map((beneficiaryData: BeneficiaryId) => ({
        beneficiaryId: beneficiaryData.id,
        idFront: beneficiaryData?.frontDocument!,
        idBack: beneficiaryData?.backDocument!,
      })),
      extraDocumentOne,
      extraDocumentTwo,
      extraDocumentThree,
    };

    const response = await updateBusinessDocuments(localState.id!, fileBody);

    setIsSubmitting(false);

    if (response.result && response.data) {
      const businessData = response.data.businessData || response.data;
      return navigate(pagesPaths.CommerceSummary, { state: { ...localState, ...businessData } });
    }

    triggerToasts(response);
  };

  useEffect(() => {
    if (
      isSubmitting &&
      cardFormDGI &&
      beneficiaryIds.filter((idFiles) => idFiles.frontDocument && idFiles.backDocument).length ===
        beneficiaries.length
    ) {
      postFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiaryIds, cardFormDGI, localState, navigate]);

  const handleGoBack = () =>
    navigate(localState?.from || pagesPaths.CommerceConditions, { state: { ...localState } });

  const handleSubmit = () => {
    if (formRefs.current) {
      const didFormRefsSubmitted: boolean = doRefsSubmissions(formRefs);
      didFormRefsSubmitted && setIsSubmitting(true);
    }
  };

  const handleFormsSubmit = () => {};

  const handleBeneficiaryIdsSubmit = (
    frontDocument: File | string | null,
    backDocument: File | string | null,
    index: number,
  ) => {
    const replaceArray = beneficiaryIds?.map((beneficiaryData: BeneficiaryId, mapIndex: number) =>
      mapIndex === index ? { ...beneficiaryData, frontDocument, backDocument } : beneficiaryData,
    );
    setBeneficiaryIds(replaceArray);
  };

  const handleFile = (
    e: ChangeEvent<HTMLInputElement>,
    name: string,
    setFieldValue: (name: string, value: number) => void,
  ) => {
    if (e?.target?.files) {
      setFieldValue(name, e.target.files.length);
      name === 'cardFormDGI' && setCardFormDGI(e.target.files[0]);
      name === 'extraDocumentOne' && setExtraDocumentOne(e.target.files[0]);
      name === 'extraDocumentTwo' && setExtraDocumentTwo(e.target.files[0]);
      name === 'extraDocumentThree' && setExtraDocumentThree(e.target.files[0]);
    }
  };

  if (isFinished) {
    return null;
  }

  return (
    <>
      <Container className="documentation">
        <ProgressBar now={getProgressPercentage('CommerceDocumentation')} />
        <Row>
          <Col lg={12}>
            <Formik
              innerRef={(formElement: any) => (formRefs.current[0] = formElement)}
              initialValues={{ cardFormDGI: localState?.cardFormDGI ? 1 : null }}
              validationSchema={schemas.ActivateCommerceSixthStepFirst}
              onSubmit={handleFormsSubmit}
            >
              <Form>
                <h1>
                  <img src={pspLogo} alt="Comercio" className="me-3" /> Adjuntar documentos
                </h1>
                <br />
                <label className="input-title" htmlFor="cardFormDGI">
                  Tarjeta de RUT o Formulario DGI 6361 - 6161 - 6351:{' '}
                  <span className="required-asterisk">*</span>
                </label>
                <FileInput name="cardFormDGI" file={cardFormDGI} setFile={handleFile} />
              </Form>
            </Formik>
            {beneficiaries?.map((beneficiary: NewBeneficiaryValues, index: number) => (
              <NewBeneficiaryId
                key={index}
                innerRef={(formElement: any) => (formRefs.current[index + 1] = formElement)}
                beneficiary={beneficiary}
                index={index}
                handleSubmit={handleBeneficiaryIdsSubmit}
              />
            ))}
            <Formik
              innerRef={(formElement: any) => (formRefs.current[0] = formElement)}
              initialValues={{
                extraDocumentOne: localState?.extraDocumentOne ? 1 : null,
                extraDocumentTwo: localState?.extraDocumentTwo ? 1 : null,
                extraDocumentThree: localState?.extraDocumentThree ? 1 : null,
              }}
              validationSchema={schemas.ActivateCommerceSixthStepFirst}
              onSubmit={handleFormsSubmit}
            >
              <Form className="extra-documents">
                <label className="input-title extra-document-input" htmlFor="extraDocumentOne">
                  Documento extra 1:
                </label>
                <FileInput name="extraDocumentOne" file={extraDocumentOne} setFile={handleFile} />
                <label className="input-title extra-document-input" htmlFor="extraDocumentTwo">
                  Documento extra 2:
                </label>
                <FileInput name="extraDocumentTwo" file={extraDocumentTwo} setFile={handleFile} />
                <label className="input-title extra-document-input" htmlFor="extraDocumentThree">
                  Documento extra 3:
                </label>
                <FileInput name="extraDocumentThree" file={extraDocumentThree} setFile={handleFile} />
              </Form>
            </Formik>
          </Col>
        </Row>
      </Container>
      <NavigationFooter previousHandler={handleGoBack} nextHandler={handleSubmit} loading={isSubmitting} />
    </>
  );
}

export default CommerceDocumentation;
